import { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, PlayCircle, AlertTriangle } from 'lucide-react';
import { toMin } from './professorUtils';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';

/**
 * Turno Auxiliar (PRE/POS) — atividades de preparação/conclusão com checklist interativo
 */
export function TurnoAuxiliar({ tipo, primeiroTurnoInicio, ultimoTurnoFim, dataSelecionada, disabled = false, isAtivo = false, agoraMin = 0 }: {
  tipo: 'PRE' | 'POS';
  primeiroTurnoInicio: string;
  ultimoTurnoFim: string;
  dataSelecionada: string;
  disabled?: boolean;
  isAtivo?: boolean;
  agoraMin?: number;
}) {
  const [expanded, setExpanded] = useState(isAtivo);
  const [atividades, setAtividades] = useState<any[]>([]);

  useEffect(() => {
    if (isAtivo) setExpanded(true);
  }, [isAtivo]);

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        // Guard: only professors can access this endpoint
        const token = (localStorage.getItem('ac_token') || sessionStorage.getItem('ac_token'));
        if (token) {
          try {
            const payload = JSON.parse(atob(token.split('.')[1]));
            if (payload.perfil && payload.perfil !== 'PROFESSOR') return;
          } catch { /* proceed if can't decode */ }
        }
        const res = await api.get(ROUTES.professor.atividadesAuxiliares.registros(dataSelecionada));
        if (mounted) {
          setAtividades(res.data.filter((a: any) => a.tipo === tipo));
        }
      } catch (err: any) {
        // Silently ignore 403 (non-professor accessing professor endpoint)
        if (err?.response?.status !== 403) {
          console.error('Erro ao carregar atividades do turno:', err);
        }
      }
    };
    fetchData();
    return () => { mounted = false };
  }, [tipo, dataSelecionada]);

  const totalMin = atividades.reduce((a, b) => a + b.duracaoMin, 0);
  const concluidasCount = atividades.filter(a => a.concluida).length;
  const todasFeitas = atividades.length > 0 && concluidasCount === atividades.length;

  let inicio: string, fim: string;
  if (tipo === 'PRE') {
    fim = primeiroTurnoInicio;
    const fimMin = toMin(fim);
    const iniMin = Math.max(0, fimMin - 40);
    inicio = `${String(Math.floor(iniMin / 60)).padStart(2, '0')}:${String(iniMin % 60).padStart(2, '0')}`;
  } else {
    inicio = ultimoTurnoFim;
    const iniMin = toMin(inicio);
    const fimMin = iniMin + 30;
    fim = `${String(Math.floor(fimMin / 60)).padStart(2, '0')}:${String(fimMin % 60).padStart(2, '0')}`;
  }

  const label = tipo === 'PRE' ? 'Preparação' : 'Conclusão';
  const dotColor = isAtivo ? (tipo === 'PRE' ? '#f59e0b' : '#8b5cf6') : '#cbd5e1';
  const labelColor = isAtivo ? (tipo === 'PRE' ? '#d97706' : '#7c3aed') : '#94a3b8';
  const duracaoStr = totalMin < 60 ? `${totalMin} min` : `${Math.floor(totalMin / 60)}h ${totalMin % 60 > 0 ? String(totalMin % 60).padStart(2, '0') + 'min' : ''}`;

  const toggleAtividade = async (id: string, estaConcluida: boolean) => {
    if (disabled) return;
    setAtividades(prev => prev.map(a => a.id === id ? { ...a, concluida: !estaConcluida } : a));
    try {
      await api.post(ROUTES.professor.atividadesAuxiliares.alternarConclusao(id, dataSelecionada));
    } catch (err) {
      console.error('Falha ao alternar atividade', err);
      setAtividades(prev => prev.map(a => a.id === id ? { ...a, concluida: estaConcluida } : a));
    }
  };

  const containerColorState = disabled ? '#e2e8f0' : (todasFeitas ? '#16a34a' : dotColor);
  const textColorState = disabled ? '#94a3b8' : (todasFeitas ? '#16a34a' : labelColor);
  const opacityState = disabled ? 0.6 : 1;

  return (
    <div style={{ marginBottom: 6 }}>
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '5px 4px', marginBottom: 2,
          cursor: 'pointer', userSelect: 'none', borderRadius: 6, transition: 'background 0.15s',
        }}
        onClick={() => setExpanded(!expanded)}
        onMouseEnter={e => { (e.currentTarget as HTMLDivElement).style.background = '#f8fafc'; }}
        onMouseLeave={e => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
      >
        <div style={{
          width: 6, height: 6, borderRadius: '50%',
          background: containerColorState, flexShrink: 0,
        }} />
        <span style={{ fontSize: '0.72rem', fontWeight: 600, color: textColorState, opacity: opacityState, textTransform: 'uppercase' }}>
          {label}
        </span>
        <span style={{ fontSize: '0.63rem', color: disabled ? '#cbd5e1' : '#b0b8c4' }}>
          {inicio} &ndash; {fim}
        </span>
        <span style={{ fontSize: '0.6rem', fontWeight: 600, color: disabled ? '#cbd5e1' : (todasFeitas ? '#16a34a' : '#b0b8c4'), marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 4 }}>
          {concluidasCount}/{atividades.length} · {duracaoStr}
          {todasFeitas && !disabled && <CheckCircle2 size={11} color="#16a34a" />}
        </span>
      </div>

      {expanded && atividades.length > 0 && (() => {
        const fimPrep = tipo === 'PRE' ? toMin(primeiroTurnoInicio) : toMin(ultimoTurnoFim) + 30;
        const deadlines: number[] = [];
        let acumulado = 0;
        for (let j = atividades.length - 1; j >= 0; j--) {
          deadlines[j] = fimPrep - acumulado;
          acumulado += (atividades[j].duracaoMin || 5);
        }
        const nenhumFeito = atividades.length > 0 && atividades.every((a: any) => !a.concluida);
        const passouPrepInteiro = tipo === 'PRE' && agoraMin >= toMin(primeiroTurnoInicio) && nenhumFeito;

        return (
        <div style={{ paddingLeft: 8, transition: 'all 0.2s' }}>
          {passouPrepInteiro && (
            <div style={{ padding: '6px 10px', marginBottom: 6, borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', fontSize: '0.72rem', color: '#b91c1c', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
              <AlertTriangle size={13} color="#dc2626" /> Nenhum item de preparação foi confirmado. Incidente registrado.
            </div>
          )}
          {atividades.map((atv, i) => {
            const feita = atv.concluida;
            const isLast = i === atividades.length - 1;
            const firstPendenteIndex = atividades.findIndex(a => !a.concluida);
            const isAtivoAtual = isAtivo && !feita && i === firstPendenteIndex;
            const isOverdue = !feita && agoraMin >= deadlines[i];

            return (
              <div key={atv.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: 20, flexShrink: 0 }}>
                  <div
                    style={{
                      width: 20, height: 20, borderRadius: '50%',
                      background: feita ? '#16a34a' : isOverdue ? '#dc2626' : (isAtivoAtual ? '#2563eb' : '#e5e7eb'),
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      cursor: 'pointer', transition: 'all 0.15s',
                      transform: feita || isAtivoAtual ? 'scale(1.05)' : 'scale(1)',
                      boxShadow: isAtivoAtual ? '0 0 0 3px rgba(37,99,235,0.2)' : isOverdue ? '0 0 0 3px rgba(220,38,38,0.15)' : 'none',
                    }}
                    onClick={(e) => { e.stopPropagation(); toggleAtividade(atv.id, feita); }}
                  >
                    {feita
                      ? <CheckCircle2 size={13} color="white" />
                      : isOverdue
                        ? <XCircle size={13} color="white" />
                        : isAtivoAtual
                          ? <PlayCircle size={13} color="white" />
                          : <div style={{ width: 8, height: 8, borderRadius: '50%', border: '2px solid #cbd5e1' }} />
                    }
                  </div>
                  {!isLast && <div style={{ width: 2, flex: 1, minHeight: 16, background: feita ? '#bbf7d0' : isOverdue ? '#fecaca' : '#e5e7eb' }} />}
                </div>
                <div
                  style={{
                    flex: 1, padding: '2px 8px 10px', borderRadius: 8, marginBottom: 2,
                    background: feita ? '#f0fdf4' : isOverdue ? '#fef2f2' : (isAtivoAtual ? '#eff6ff' : 'transparent'),
                    border: isOverdue ? '1px solid #fecaca' : isAtivoAtual ? '1px solid #bfdbfe' : '1px solid transparent',
                    cursor: 'pointer', transition: 'background 0.15s',
                  }}
                  onClick={(e) => { e.stopPropagation(); toggleAtividade(atv.id, feita); }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: '0.8rem' }}>{atv.icone}</span>
                      <span style={{
                        fontWeight: 600, fontSize: '0.8rem',
                        color: feita ? '#16a34a' : isOverdue ? '#dc2626' : (isAtivoAtual ? '#1d4ed8' : 'var(--text-main, #1e293b)'),
                        textDecoration: feita ? 'line-through' : 'none',
                        opacity: feita ? 0.7 : 1,
                      }}>
                        {atv.nome}
                      </span>
                    </div>
                    <span style={{ fontSize: '0.63rem', color: '#94a3b8', fontFamily: 'monospace', fontWeight: 500, flexShrink: 0 }}>
                      {atv.duracaoMin} min
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        );
      })()}
    </div>
  );
}
